import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, ShieldCheck, ShieldX, Loader2, ChevronDown, ChevronUp, Ban, Plus } from "lucide-react";
import { MultiSelectDropdown } from "@/components/MultiSelectDropdown";

function ResultLine({ result, kind, testid }) {
  if (!result) return null;
  // Exclusion endpoints expose `excluded` + `match_count`; DNC endpoints
  // expose `matched` + `count`. Normalise so the same component renders both.
  const isHit = kind === "dnc" ? !!result.matched : !!result.excluded;
  const count = kind === "dnc" ? (result.count ?? 0) : (result.match_count ?? 0);
  const tone = isHit ? "badge-error" : "badge-success";
  const Icon = isHit ? (kind === "dnc" ? Ban : ShieldX) : ShieldCheck;
  const label = kind === "dnc" ? "DNC" : "Excluded";
  return (
    <div className={`mt-3 px-3 py-2 rounded-sm flex items-center gap-2 ${tone}`} data-testid={testid || "quick-result"}>
      <Icon size={14} />
      <span className="text-sm">
        {isHit
          ? `${label} · ${count} match${count > 1 ? "es" : ""} for ${result.type}=${result.value}`
          : `Clean · no ${kind === "dnc" ? "DNC entry" : "match"} for ${result.type}=${result.value}`}
      </span>
      {isHit && kind !== "dnc" && result.matches?.[0] && (
        <span className="font-mono text-[10px] ml-auto text-muted-foreground">
          first: {result.matches[0].work_email} · {result.matches[0].campaign_name || "—"}
        </span>
      )}
      {isHit && kind === "dnc" && result.items?.[0] && (
        <span className="font-mono text-[10px] ml-auto text-muted-foreground">
          first: {result.items[0].email || result.items[0].domain} {result.items[0].campaign_name ? `· ${result.items[0].campaign_name}` : ""}
        </span>
      )}
    </div>
  );
}

function MasterSearch({ endpoint, addEndpoint, idPrefix, kind, placeholderEmail, placeholderDomain }) {
  const [type, setType] = useState("email");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setError(""); setResult(null);
    if (!value.trim()) return setError("Enter a value to check.");
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, { type, value });
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const add = async () => {
    setError("");
    if (!value.trim()) return setError("Enter a value to add.");
    setAdding(true);
    try {
      const { data } = await api.post(addEndpoint, { type, value });
      if (data.duplicate) {
        toast.info(`Already on the DNC list: ${type}=${data.value}`);
      } else {
        toast.success(`Added to Master DNC: ${type}=${data.value}`);
      }
      // Refresh the search result so the agent sees the new state.
      await run();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? formatApiErrorDetail(detail) : (e?.message || "Add failed"));
    } finally { setAdding(false); }
  };

  return (
    <div>
      <Tabs value={type} onValueChange={(v) => { setType(v); setResult(null); }}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="email" data-testid={`${idPrefix}-tab-email`} className="rounded-sm text-xs h-8">By Email</TabsTrigger>
          <TabsTrigger value="domain" data-testid={`${idPrefix}-tab-domain`} className="rounded-sm text-xs h-8">By Domain</TabsTrigger>
        </TabsList>
        <TabsContent value="email" />
        <TabsContent value="domain" />
      </Tabs>
      <div className="flex gap-2 mt-2">
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
          <Input
            data-testid={`${idPrefix}-input`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), run())}
            placeholder={type === "email" ? placeholderEmail : placeholderDomain}
            className="h-9 rounded-sm pl-8 font-mono text-sm"
          />
        </div>
        <Button type="button" onClick={run} disabled={loading} className="rounded-sm h-9 bg-primary hover:bg-[#00227A]" data-testid={`${idPrefix}-run`}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Check"}
        </Button>
        {addEndpoint && (
          <Button type="button" onClick={add} disabled={adding || !value.trim()} variant="outline" className="rounded-sm h-9 border-red-300 text-red-600 hover:bg-red-50" data-testid={`${idPrefix}-add`}>
            {adding ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Add</>}
          </Button>
        )}
      </div>
      {error && <div className="badge-error px-3 py-2 rounded-sm text-xs mt-2" data-testid={`${idPrefix}-error`}>{error}</div>}
      <ResultLine result={result} kind={kind} testid={`${idPrefix}-result`} />
    </div>
  );
}

function CampaignSearch({ endpoint, addEndpoint, idPrefix, kind, placeholderEmail, placeholderDomain }) {
  const { options } = useAuth();
  const campaigns = options?.campaigns ?? [];
  const [scope, setScope] = useState([]);
  const [type, setType] = useState("email");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setError(""); setResult(null);
    if (scope.length === 0) return setError("Select at least one campaign.");
    if (!value.trim()) return setError("Enter a value to check.");
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, { scope, type, value });
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const add = async () => {
    setError("");
    if (scope.length === 0) return setError("Select at least one campaign to add into.");
    if (!value.trim()) return setError("Enter a value to add.");
    setAdding(true);
    try {
      // Add once per campaign in scope so the agent can flag a domain across
      // several campaigns at once. Report per-campaign success/duplicate so
      // the toast is actually useful.
      let added = 0, dup = 0;
      for (const camp of scope) {
        const { data } = await api.post(addEndpoint, { campaign_name: camp, type, value });
        if (data.duplicate) dup += 1; else added += 1;
      }
      if (added && dup) {
        toast.success(`Added ${added}, already-present ${dup} (${type}=${value})`);
      } else if (added) {
        toast.success(`Added to Campaign DNC for ${added} campaign${added > 1 ? "s" : ""}`);
      } else {
        toast.info(`Already on the Campaign DNC list for all ${dup} selected campaign${dup > 1 ? "s" : ""}`);
      }
      await run();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? formatApiErrorDetail(detail) : (e?.message || "Add failed"));
    } finally { setAdding(false); }
  };

  return (
    <div>
      <MultiSelectDropdown
        label={`Scope (${scope.length} selected)`}
        options={campaigns}
        value={scope}
        onChange={setScope}
        placeholder={campaigns.length === 0 ? "No campaigns available." : "Select campaigns to check…"}
        testid={`${idPrefix}-scope`}
        maxHeight="240px"
      />
      <Tabs value={type} onValueChange={(v) => { setType(v); setResult(null); }} className="mt-2">
        <TabsList className="rounded-sm">
          <TabsTrigger value="email" data-testid={`${idPrefix}-tab-email`} className="rounded-sm text-xs h-8">By Email</TabsTrigger>
          <TabsTrigger value="domain" data-testid={`${idPrefix}-tab-domain`} className="rounded-sm text-xs h-8">By Domain</TabsTrigger>
        </TabsList>
        <TabsContent value="email" />
        <TabsContent value="domain" />
      </Tabs>
      <div className="flex gap-2 mt-2">
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
          <Input
            data-testid={`${idPrefix}-input`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), run())}
            placeholder={type === "email" ? placeholderEmail : placeholderDomain}
            className="h-9 rounded-sm pl-8 font-mono text-sm"
          />
        </div>
        <Button type="button" onClick={run} disabled={loading} className="rounded-sm h-9 bg-primary hover:bg-[#00227A]" data-testid={`${idPrefix}-run`}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Check"}
        </Button>
        {addEndpoint && (
          <Button type="button" onClick={add} disabled={adding || !value.trim() || scope.length === 0} variant="outline" className="rounded-sm h-9 border-red-300 text-red-600 hover:bg-red-50" data-testid={`${idPrefix}-add`}>
            {adding ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Add</>}
          </Button>
        )}
      </div>
      {error && <div className="badge-error px-3 py-2 rounded-sm text-xs mt-2" data-testid={`${idPrefix}-error`}>{error}</div>}
      <ResultLine result={result} kind={kind} testid={`${idPrefix}-result`} />
    </div>
  );
}

export default function QuickExclusionPanel({ defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border bg-secondary/30 rounded-sm" data-testid="quick-exclusion-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-secondary/60"
        data-testid="quick-exclusion-toggle"
      >
        <div className="text-left">
          <div className="meta-label">Pre-submission Checks</div>
          <div className="font-display font-bold text-sm">Exclusions &amp; Do-Not-Contact Lookup</div>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
          <section data-testid="quick-master-section">
            <div className="meta-label mb-2">Master Exclusion Search</div>
            <MasterSearch
              endpoint="/exclusion/master"
              idPrefix="quick-master"
              kind="exclusion"
              placeholderEmail="Quick-check an email…"
              placeholderDomain="Quick-check a domain…"
            />
          </section>
          <section data-testid="quick-campaign-section">
            <div className="meta-label mb-2">Campaign Exclusion Search</div>
            <CampaignSearch
              endpoint="/exclusion/campaign"
              idPrefix="quick-campaign"
              kind="exclusion"
              placeholderEmail="Quick-check email in scope…"
              placeholderDomain="Quick-check domain in scope…"
            />
          </section>
          <section data-testid="quick-master-dnc-section">
            <div className="meta-label mb-2 flex items-center gap-1.5"><Ban size={12} /> Master DNC Search</div>
            <MasterSearch
              endpoint="/dnc/master/search"
              addEndpoint="/dnc/master/add"
              idPrefix="quick-master-dnc"
              kind="dnc"
              placeholderEmail="Quick-check email against DNC…"
              placeholderDomain="Quick-check domain against DNC…"
            />
          </section>
          <section data-testid="quick-campaign-dnc-section">
            <div className="meta-label mb-2 flex items-center gap-1.5"><Ban size={12} /> Campaign DNC Search</div>
            <CampaignSearch
              endpoint="/dnc/campaign/search"
              addEndpoint="/dnc/campaign/add"
              idPrefix="quick-campaign-dnc"
              kind="dnc"
              placeholderEmail="Quick-check email against campaign DNC…"
              placeholderDomain="Quick-check domain against campaign DNC…"
            />
          </section>
        </div>
      )}
    </div>
  );
}
